import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faEdit,
  faPlus,
  faList,
  faDownload,
  faDatabase
} from "@fortawesome/free-solid-svg-icons";

const Eisenhower = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [view, setView] = useState("home");
  const [records, setRecords] = useState([]);
  const [selected, setSelected] = useState(null);

  const [showGraph, setShowGraph] = useState(false);

  const [form, setForm] = useState({
    title: "",
    brief: "",
    tasks: [],
    remarks: ""
  });

  const [task, setTask] = useState({
    title: "",
    category: "UI"
  });

  const categories = [
    { key: "UI", label: "Urgent & Important" },
    { key: "UNI", label: "Urgent but Not Important" },
    { key: "IUN", label: "Important but Not Urgent" },
    { key: "NINU", label: "Not Important & Not Urgent" }
  ];

  const sampleTasks = [
    // 🔴 Urgent & Important (UI)
    { title: "Submit client proposal before deadline", category: "UI" },
    { title: "Fix production bug impacting users", category: "UI" },
    { title: "Prepare for today’s critical meeting", category: "UI" },
    { title: "Pay electricity bill before due date", category: "UI" },
    { title: "Handle urgent customer complaint", category: "UI" },

    // 🟡 Urgent but Not Important (UNI)
    { title: "Respond to non-critical emails", category: "UNI" },
    { title: "Attend unnecessary meeting invite", category: "UNI" },
    { title: "Follow up on minor admin tasks", category: "UNI" },
    { title: "Quick phone calls interrupting work", category: "UNI" },
    { title: "Coordinate routine team updates", category: "UNI" },

    // 🟢 Important but Not Urgent (IUN)
    { title: "Plan quarterly business strategy", category: "IUN" },
    { title: "Learn new skill / upskilling", category: "IUN" },
    { title: "Build long-term client relationships", category: "IUN" },
    { title: "Health & fitness routine", category: "IUN" },
    { title: "Financial planning & investments", category: "IUN" },

    // ⚪ Not Important & Not Urgent (NINU)
    { title: "Scrolling social media endlessly", category: "NINU" },
    { title: "Watching random YouTube videos", category: "NINU" },
    { title: "Unplanned gossip chats", category: "NINU" },
    { title: "Checking phone notifications repeatedly", category: "NINU" },
    { title: "Procrastinating low-value tasks", category: "NINU" }
  ];

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("eisenhower") || "[]");
    setRecords(saved);
  }, []);

  const saveLocal = (data) => {
    localStorage.setItem("eisenhower", JSON.stringify(data));
    setRecords(data);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const loadSample = () => {
    const sample = {
      id: Date.now(),
      title: "Daily Productivity Plan",
      brief: "Balanced Eisenhower Matrix with realistic tasks",
      date: new Date().toLocaleString(),
      tasks: sampleTasks, // ✅ reuse
      remarks:
        "Focus more on Important but Not Urgent tasks to avoid future crises."
    };

    setSelected(sample);
    setView("matrix");
  };

  const addTask = () => {
    if (!task.title) return;
    setForm({ ...form, tasks: [...form.tasks, task] });
    setTask({ title: "", category: "UI" });
  };

  const saveDraft = () => {
    const newRec = {
      ...form,
      id: Date.now(),
      date: new Date().toLocaleString()
    };
    const updated = [...records, newRec];
    saveLocal(updated);
    setView("list");
  };

  const deleteRecord = (id) => {
    const updated = records.filter((r) => r.id !== id);
    saveLocal(updated);
  };

  const editRecord = (rec) => {
    setForm(rec);
    setView("create");
  };

  const downloadPDF = () => {
    window.print(); // simple mock
  };

  const groupTasks = (cat) =>
    (selected?.tasks || []).filter((t) => t.category === cat);

  return (
    <div className="min-h-screen flex flex-col bg-black text-gray-100">
      <header className="sticky top-0 bg-black border-b border-blue-400 px-4 py-3 flex justify-between">
        <div>
          <div className="text-lg lg:text-xl text-yellow-200 font-bold">Urgent-Important Matrix</div>
          <div className="text-xs text-gray-400">Eisenhower Task Prioritization</div>
        </div>
        <div>
          {isLoggedIn && (
            <button onClick={handleLogout} className="text-red-400">⏻</button>
          )}
        </div>
      </header>

      {/* ACTION BUTTONS */}
      <div className="p-2 grid grid-cols-3 gap-2">
        <button onClick={loadSample} className="bg-blue-600 px-2 py-1 rounded">Load Sample</button>
        <button onClick={() => setView("list")} className="bg-green-600 px-2 py-1 rounded">
          <FontAwesomeIcon icon={faList}/> My List
        </button>
        <button onClick={() => setView("create")} className="bg-purple-600 px-2 py-1 rounded">
          <FontAwesomeIcon icon={faPlus}/> Create New
        </button>
      </div>

      <div className="min-h-[70vh] p-2 space-y-2">
        {/* LIST VIEW */}
        {view === "list" && (
          <div className="space-y-3">
            {records.map((r) => (
              <div key={r.id} className="border p-3 rounded bg-gray-900">
                <div onClick={() => {setSelected(r); setView("detail");}} className="cursor-pointer">
                  <div className="font-bold">{r.title}</div>
                  <div className="text-xs">{r.brief}</div>
                  <div className="text-xs text-gray-400">{r.date}</div>
                </div>
                <div className="flex gap-3 mt-2">
                  <button onClick={() => editRecord(r)}><FontAwesomeIcon icon={faEdit}/></button>
                  <button onClick={() => deleteRecord(r.id)}><FontAwesomeIcon icon={faTrash}/></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* DETAIL VIEW */}
        {view === "detail" && selected && (
          <div className="bg-gray-900 p-2 rounded">
            <h2 className="font-bold text-lg">{selected.title}</h2>
            <p className="text-sm">{selected.brief}</p>

            <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
              {categories.map((c) => (
                <div key={c.key} className="border p-2 rounded">
                  <div className="font-semibold">{c.label}</div>
                  {groupTasks(c.key).map((t, i) => (
                    <div key={i}>• {t.title}</div>
                  ))}
                </div>
              ))}
            </div>

            <button onClick={() => setView("matrix")} className="mt-4 bg-yellow-600 px-3 py-2 rounded">
              View Matrix
            </button>
          </div>
        )}

        {/* MATRIX VIEW */}
        {view === "matrix" && selected && (
          <div className="space-y-4">
            <button
              onClick={() => setShowGraph((s) => !s)}
              className="bg-blue-600 px-3 py-2 rounded"
            >
              {showGraph ? "Hide Graph" : "Show Graph"}
            </button>

            {!showGraph && (
              <div className="grid grid-cols-2 gap-3 text-xs">
                {categories.map((c) => (
                  <div key={c.key} className={`p-3 rounded border ${c.color}`}>
                    <div className="font-bold">{c.label}</div>
                    <div className="text-[10px] text-gray-400 mb-2">{c.action}</div>
                    {groupTasks(c.key).map((t, i) => (
                      <div key={i}>• {t.title}</div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {showGraph && (
              <div className="relative w-full max-w-3xl mx-auto">
                <div className="grid grid-cols-2 grid-rows-2 gap-2 text-xs">
                  <div className="p-4 rounded-xl border bg-red-900 border-red-500">
                    <div className="font-bold text-sm">Important & Urgent</div>
                    <div className="text-[10px] mb-2">DO</div>
                    {groupTasks("UI").map((t,i)=><div key={i}>• {t.title}</div>)}
                  </div>
                  <div className="p-4 rounded-xl border bg-yellow-900 border-yellow-500">
                    <div className="font-bold text-sm">Important not Urgent</div>
                    <div className="text-[10px] mb-2">DECIDE</div>
                    {groupTasks("IUN").map((t,i)=><div key={i}>• {t.title}</div>)}
                  </div>
                  <div className="p-4 rounded-xl border bg-green-900 border-green-500">
                    <div className="font-bold text-sm">Urgent not Important</div>
                    <div className="text-[10px] mb-2">DELEGATE</div>
                    {groupTasks("UNI").map((t,i)=><div key={i}>• {t.title}</div>)}
                  </div>
                  <div className="p-4 rounded-xl border bg-gray-800 border-gray-500">
                    <div className="font-bold text-sm">Not Important not Urgent</div>
                    <div className="text-[10px] mb-2">DELETE</div>
                    {groupTasks("NINU").map((t,i)=><div key={i}>• {t.title}</div>)}
                  </div>
                </div>
                <div className="absolute top-1/2 left-0 -translate-y-1/2 -rotate-90 text-[10px] text-cyan-400">
                  IMPORTANT ↑
                </div>
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-[10px] text-cyan-400">
                  URGENT →
                </div>
              </div>
            )}
          </div>
        )}

        {/* CREATE */}
        {view === "create" && (
          <div className="space-y-3">
            <input
              placeholder="Title"
              className="w-full p-2 bg-black border"
              value={form.title}
              onChange={(e)=>setForm({...form,title:e.target.value})}
            />

            <textarea
              placeholder="Brief"
              className="w-full p-2 bg-black border"
              value={form.brief}
              onChange={(e)=>setForm({...form,brief:e.target.value})}
            />

            {/* TASK ADD */}
            <div className="flex gap-2">
              <input
                placeholder="Task"
                className="flex-1 p-2 bg-black border"
                value={task.title}
                onChange={(e)=>setTask({...task,title:e.target.value})}
              />
              <select
                value={task.category}
                onChange={(e)=>setTask({...task,category:e.target.value})}
                className="bg-black border p-2"
              >
                {categories.map(c => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
              <button onClick={addTask} className="bg-green-600 px-3">+</button>
            </div>

            {/* TASK LIST */}
            <div className="text-xs">
              {form.tasks.map((t,i)=>(
                <div key={i}>• {t.title} ({t.category})</div>
              ))}
            </div>

            <textarea
              placeholder="Final Remarks"
              className="w-full p-2 bg-black border"
              value={form.remarks}
              onChange={(e)=>setForm({...form,remarks:e.target.value})}
            />

            <div className="flex gap-2 flex-wrap">
              <button onClick={saveDraft} className="bg-blue-600 px-3 py-2 rounded">Save Draft</button>
              <button disabled className="bg-gray-600 px-3 py-2 rounded cursor-not-allowed">
                <FontAwesomeIcon icon={faDatabase}/> Save To DB
              </button>
              <button onClick={downloadPDF} className="bg-yellow-600 px-3 py-2 rounded">
                <FontAwesomeIcon icon={faDownload}/> Download PDF
              </button>
            </div>
          </div>
        )}
      </div>

       <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2025 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Eisenhower;